import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import logo from "../images/logo_transparent.png";
import "../styles/home.css";

const PHONE = "+919080122817";
const MAPS_URL = `http://googleusercontent.com/maps/google.com/0`;
const FORM_ENDPOINT = "https://formsubmit.co/ajax/b2btradersb2@gmail.com";

// ---- Small helpers ----------------------------------------------------------
export const normalizeHeader = (s = "") =>
  s
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w]/g, "");

export const parseWorkbookToRows = (wb) => {
  // first sheet only
  const wsName = wb.SheetNames[0];
  const ws = wb.Sheets[wsName];
  // raw JSON rows
  const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

  // Normalize columns for common headers like Name, SKU, Brand, Category, Unit, Price
  const candidateMap = {
    name: ["name", "item", "product", "item_name", "description"],
    sku: ["sku", "code", "item_code", "product_code"],
    brand: ["brand", "make", "manufacturer"],
    category: ["category", "group", "segment", "type"],
    unit: ["unit", "pack", "uom", "size"],
    price: ["price", "mrp", "rate"],
  };

  // Build a key map from the first row's headers
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const normHeaders = headers.map(normalizeHeader);

  const keyMap = {};
  for (const [std, candidates] of Object.entries(candidateMap)) {
    const idx = normHeaders.findIndex((h) => candidates.includes(h));
    if (idx !== -1) keyMap[std] = headers[idx];
  }

  // Fallback: if no typical headers found, just keep all columns
  const mapped = rows.map((r, i) => ({
    id: i + 1,
    name: keyMap.name ? r[keyMap.name] : r[headers[0]] ?? "",
    sku: keyMap.sku ? r[keyMap.sku] : "",
    brand: keyMap.brand ? r[keyMap.brand] : "",
    category: keyMap.category ? r[keyMap.category] : "",
    unit: keyMap.unit ? r[keyMap.unit] : "",
    price: keyMap.price ? r[keyMap.price] : "",
    _raw: r, // keep everything in case you want to include more later
  }));

  return mapped;
};

export const buildCompactList = (items) =>
  items
    .map((it) => {
      const bits = [it.name];
      if (it.sku) bits.push(`SKU: ${it.sku}`);
      if (it.brand) bits.push(`Brand: ${it.brand}`);
      if (it.unit) bits.push(`Unit: ${it.unit}`);
      return `${bits.join(" / ")}  x ${it.qty}`;
    })
    .join(" ;\n");

// ---- The searchable table + stack (with pagination, no upload) -------------
function ItemsPicker({ onSelectionChange, excelUrl = "/items.xlsx" }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [stack, setStack] = useState([]); // { id, name, brand, category, unit, price, qty }

  // Pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  // Load from network (public/items.xlsx) by default
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(excelUrl, { cache: "no-store" });
        if (!res.ok) throw new Error("Failed to fetch Excel");
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const rows = parseWorkbookToRows(wb);
        if (!cancelled) setItems(rows);
      } catch (err) {
        console.error("Excel load failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [excelUrl]);

  // Filter by query across key fields (SKU removed from UI, still searchable if present)
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((r) =>
      [r.name, r.sku, r.brand, r.category, r.unit]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q))
    );
  }, [items, query]);

  // Pagination bookkeeping
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => setPage(1), [query]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = startIdx + PAGE_SIZE;
  const visibleRows = filtered.slice(startIdx, endIdx);

  // Keep parent informed (so form can attach selection)
  useEffect(() => {
    onSelectionChange?.(stack);
  }, [stack, onSelectionChange]);

  // Stack ops
  const addToStack = (row) =>
    setStack((s) => {
      const exists = s.find((it) => it.id === row.id);
      if (exists) {
        return s.map((it) => (it.id === row.id ? { ...it, qty: it.qty + 1 } : it));
      }
      const next = [
        ...s,
        {
          id: row.id,
          name: row.name,
          sku: row.sku,
          brand: row.brand,
          category: row.category,
          unit: row.unit,
          price: row.price,
          qty: 1,
        },
      ];
      return next;
    });

  const inc = (id) => setStack((s) => s.map((it) => (it.id === id ? { ...it, qty: it.qty + 1 } : it)));

  const dec = (id) =>
    setStack((s) =>
      s
        .map((it) => (it.id === id ? { ...it, qty: Math.max(0, it.qty - 1) } : it))
        .filter((it) => it.qty > 0)
    );

  const remove = (id) => setStack((s) => s.filter((it) => it.id !== id));

  return (
    <section id="items" className="container section">
      <div className="section-head">
        <h3>Browse Items</h3>
        <p className="muted">Search and add items to your enquiry. Quantities can be adjusted inline.</p>
      </div>

      <div className="items-toolbar">
        <input
          className="field search"
          type="search"
          placeholder="Search by name, brand, category…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search items"
        />
        <a href="#getintouch" className="btn sm btn-ghost">Go to Enquiry</a>
      </div>

      <div className="table-wrap" role="region" aria-label="Items table">
        <table className="table" style={{ tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            <col style={{ width: "35%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "35%" }} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Brand</th>
              <th scope="col">Category</th>
              <th scope="col">Controls</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted">No items found. Try a different search.</td>
              </tr>
            ) : (
              visibleRows.map((row) => {
                const sel = stack.find((it) => it.id === row.id);
                return (
                  <tr key={row.id}>
                    <td>
                      <div className="item-name">{row.name}</div>
                      {row.sku ? <div className="tiny muted">SKU: {row.sku}</div> : null}
                      {row.unit ? <div className="tiny muted">Unit: {row.unit}</div> : null}
                    </td>
                    <td>{row.brand}</td>
                    <td>{row.category}</td>
                    <td>
                      <div className="controls-container">
                        {!sel ? (
                          <button
                            className="btn sm add-btn"
                            type="button"
                            onClick={() => addToStack(row)}
                            aria-label={`Add ${row.name}`}
                          >
                            Add to Enquiry
                          </button>
                        ) : (
                          <div className="qty-controls" aria-label={`${row.name} quantity controls`}>
                            <button
                              className="qty-btn"
                              type="button"
                              onClick={() => dec(row.id)}
                              aria-label={`Decrease ${row.name} quantity`}
                            >
                              -
                            </button>
                            <input
                              className="qty-input"
                              type="number"
                              min={1}
                              value={sel.qty}
                              onChange={(e) => {
                                const v = Math.max(1, parseInt(e.target.value || "1", 10));
                                setStack((s) => s.map((x) => (x.id === row.id ? { ...x, qty: v } : x)));
                              }}
                              aria-label={`${row.name} quantity`}
                            />
                            <button
                              className="qty-btn"
                              type="button"
                              onClick={() => inc(row.id)}
                              aria-label={`Increase ${row.name} quantity`}
                            >
                              +
                            </button>
                            <button
                              className="remove-btn"
                              type="button"
                              onClick={() => remove(row.id)}
                              aria-label={`Remove ${row.name}`}
                            >
                              x
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <nav className="pagination" aria-label="Items pagination" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
        <button
          className="btn sm"
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          aria-label="Previous page"
        >
          Prev
        </button>

        <span className="muted" aria-live="polite" id="getintouch">
          Page {page} of {totalPages} · Showing {filtered.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, filtered.length)} of {filtered.length}
        </span>

        <button
          className="btn sm"
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          aria-label="Next page"
        >
          Next
        </button>
      </nav>

    </section>
  );
}

const multiSelectStyles = `
  .custom-multiselect {
    position: relative;
    width: 100%;
  }
  .multiselect-trigger {
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    font-size: 14px;
    text-align: left;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .multiselect-trigger:hover {
    border-color: #d1d5db;
  }
  .multiselect-trigger:focus,
  .multiselect-trigger.open {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  .multiselect-trigger.error {
    border-color: #ef4444;
  }
  .multiselect-text {
    flex: 1;
    color: #374151;
  }
  .multiselect-trigger:placeholder-shown .multiselect-text {
    color: #9ca3af;
  }
  .multiselect-arrow {
    width: 20px;
    height: 20px;
    color: #6b7280;
    transition: transform 0.2s;
  }
  .multiselect-trigger.open .multiselect-arrow {
    transform: rotate(180deg);
  }
  .multiselect-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 50;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    max-height: 240px;
    overflow-y: auto;
    margin-top: 4px;
  }
  .multiselect-option {
    display: flex;
    align-items: center;
    padding: 12px;
    cursor: pointer;
    transition: background-color 0.15s;
    border-bottom: 1px solid #f3f4f6;
  }
  .multiselect-option:last-child {
    border-bottom: none;
  }
  .multiselect-option:hover {
    background-color: #f9fafb;
  }
  .multiselect-checkbox {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
  .multiselect-checkmark {
    width: 20px;
    height: 20px;
    border: 2px solid #d1d5db;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    transition: all 0.2s;
    color: white;
  }
  .multiselect-option input:checked + .multiselect-checkmark {
    background-color: #3b82f6;
    border-color: #3b82f6;
  }
  .multiselect-label {
    flex: 1;
    font-size: 14px;
    color: #374151;
  }
  .field-label {
    display: block;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
    font-size: 14px;
  }
`;

function MultiSelectDropdown({ options, value = [], onChange, placeholder = "Select options...", error = "", onValidate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState(value);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedValues(value || []);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (optionValue) => {
    const newSelected = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newSelected);
    onChange?.(newSelected);
    onValidate?.();
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) return selectedValues[0];
    return `${selectedValues.length} categories selected`;
  };

  return (
    <div className="custom-multiselect" ref={dropdownRef}>
      {selectedValues.map((val, idx) => (
        <input key={idx} type="hidden" name="category" value={val} />
      ))}
      <button
        type="button"
        className={`multiselect-trigger ${error ? 'error' : ''} ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="multiselect-text">{getDisplayText()}</span>
        <svg className="multiselect-arrow" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {isOpen && (
        <div className="multiselect-dropdown" role="listbox" aria-multiselectable="true">
          {options.map((option) => (
            <label
              key={option}
              className="multiselect-option"
              role="option"
              aria-selected={selectedValues.includes(option)}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleToggleOption(option)}
                className="multiselect-checkbox"
              />
              <span className="multiselect-checkmark">
                {selectedValues.includes(option) && (
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </span>
              <span className="multiselect-label">{option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}


// ✅ 1. Created a custom hook to get the window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
      });
    }

    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};


export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [pickerKey, setPickerKey] = useState(0);

  // ✅ 2. Use the hook to get screen width and determine if it's mobile
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [errors, setErrors] = useState({
    user_name: "",
    user_phone: "",
    business_name: "",
    categories: "",
  });

  const categoriesList = [
    "Wheat & Flours",
    "Oils & Ghee",
    "Cocoa & Chocolate",
    "Spices & Masalas",
    "Sugars & Sweeteners",
    "Dry Fruits & Nuts",
    "Dairy & Powders",
    "Pulses & Lentils",
    "Packaging & Consumables",
    "Additives & Preservatives",
    "Others",
  ];

  const validate = () => {
    const fd = new FormData(formRef.current);
    const name = (fd.get("user_name") || "").toString().trim();
    const phone = (fd.get("user_phone") || "").toString().trim();
    const biz = (fd.get("business_name") || "").toString().trim();
    const cats = fd.getAll("category");

    const phoneRegex = /^\+?\d{7,15}$/;
    const needCategories = selectedItems.length === 0;

    const nextErrors = {
      user_name: name ? "" : "Your name is required.",
      user_phone: phone ? (phoneRegex.test(phone) ? "" : "Enter a valid phone number (digits only).") : "Phone number is required.",
      business_name: biz ? "" : "Business name is required.",
      categories: needCategories ? (cats.length ? "" : "Please select at least one category.") : "",
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).some(err => err);
  };

  const focusFirstInvalid = (invalidNames) => {
    if (!invalidNames.length) return;
    const nameMap = {
      user_name: "user_name",
      user_phone: "user_phone",
      business_name: "business_name",
      categories: "category",
    };
    const targetName = nameMap[invalidNames[0]];
    const el = formRef.current?.querySelector(`[name="${targetName}"]`);
    el?.focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    const invalid = validate();
    if (invalid) {
      setStatus({ type: "error", msg: "Please fill all required fields." });
      // You might want to re-implement focusFirstInvalid if needed
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData(formRef.current);

      if (selectedItems.length > 0) {
        fd.set("items_count", String(selectedItems.length));
        fd.set("items_compact", buildCompactList(selectedItems));
        fd.delete("category");
      } else {
        const categories = fd.getAll("category");
        if (categories.length > 0) {
          fd.set("Categories_of_Interest", categories.join("\n"));
        }
        fd.delete("category");
      }

      const message = (fd.get("message") || "").toString().trim();
      if (!message) {
        fd.delete("message");
      }

      const finalFd = new FormData();
      const fieldLabelMap = {
        user_name: "Customer Name",
        user_phone: "Phone Number",
        business_name: "Business Name",
        items_count: "Total Items Selected",
        items_compact: "Selected Items List",
        Categories_of_Interest: "Categories of Interest",
        message: "Message",
      };

      for (const [key, value] of fd.entries()) {
        if (fieldLabelMap[key]) {
          finalFd.append(fieldLabelMap[key], value);
        } else {
          finalFd.append(key, value);
        }
      }

      const name = (fd.get("user_name") || "").toString();
      const phone = (fd.get("user_phone") || "").toString();
      finalFd.set("_subject", `New Enquiry • ${name} (${phone})`);

      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: finalFd,
      });

      if (!res.ok) throw new Error("Request failed");
      setStatus({ type: "success", msg: "Thanks! We'll contact you shortly." });
      formRef.current.reset();
      setErrors({ user_name: "", user_phone: "", business_name: "", categories: "" });
      setSelectedCategories([]);
      setPickerKey(k => k + 1);

    } catch (err) {
      console.error(err);
      setStatus({ type: "error", msg: "Couldn't send. Please try again or call us." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBlur = (e) => {
    const name = e.target.name;
    if (!["user_name", "user_phone", "business_name", "category"].includes(name)) return;
    validate();
  };

  const handleCategoryChange = () => validate();
  const needCategories = selectedItems.length === 0;

  // ✅ 3. Define the style object based on screen size
  const formGridStyle = {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
  };

  return (
    <div className={`b2b-root ${menuOpen ? "nav-open" : ""}`}>
      <style dangerouslySetInnerHTML={{ __html: multiSelectStyles }} />

      <header className="header" aria-label="Main header">
        <div className="container header-inner">
          <a className="brand" href="#top" aria-label="B2B Traders Home">
            <img src={logo} alt="Logo" style={{ width: 40, height: 40 }} />
            <div className="brand-text">
              <h1>B2B Traders</h1>
              <p className="muted">Wholesale Raw Materials</p>
            </div>
          </a>
          <nav className="nav" aria-label="Primary">
            <a href="#categories">Categories</a>
            <a href="#industries">Industries</a>
            <a href="#why">Why Us</a>
            <a href="#items">Items</a>
            <a href="#getintouch" className="nav-cta">Get in Touch</a>
          </nav>
          <button
            className="hamburger"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
        <nav id="mobile-menu" className="mobile-drawer" aria-label="Mobile navigation">
          <a onClick={() => setMenuOpen(false)} href="#categories">Categories</a>
          <a onClick={() => setMenuOpen(false)} href="#industries">Industries</a>
          <a onClick={() => setMenuOpen(false)} href="#why">Why Us</a>
          <a onClick={() => setMenuOpen(false)} href="#items">Items</a>
          <a onClick={() => setMenuOpen(false)} href="#getintouch" className="nav-cta">Get in Touch</a>
        </nav>
      </header>

      <section className="hero container" id="top">
        <div className="hero-content">
          <h2>
            Best <span className="accent">Raw Materials</span> for Bakeries,
            Sweet Shops & Food Businesses
          </h2>
          <p className="lead">Reliable supply, consistent quality, and fair wholesale pricing — delivered on time, every time.</p>
          <div className="hero-actions">
            <a href={`tel:${PHONE}`} className="btn btn-primary" aria-label="Call now">Call Now</a>
            <a href={MAPS_URL} target="_blank" rel="noreferrer" className="btn" aria-label="Open location in Google Maps">Find Us on Map</a>
            <a href="#getintouch" className="btn btn-ghost" aria-label="Request an enquiry">Request an Enquiry</a>
          </div>
          <div className="stats" aria-label="Business stats">
            <div className="stat"><div className="stat-val">50+</div><div className="stat-label">Active Business Clients</div></div>
            <div className="stat"><div className="stat-val">700+</div><div className="stat-label">SKUs Ready to Supply</div></div>
            <div className="stat"><div className="stat-val">24–48 hrs</div><div className="stat-label">Typical Fulfilment</div></div>
          </div>
        </div>
        <div className="hero-art" aria-hidden="true">
          <div className="glass-card"><div className="glass-title">Bulk & Consistent</div><div className="glass-sub">Flour • Sugar • Ghee • Oils • Spices • Masalas</div></div>
          <div className="glass-card delay"><div className="glass-title">Pan-India Sourcing</div><div className="glass-sub">Reliable brands & quality</div></div>
        </div>
      </section>

      <section id="categories" className="container section">
        <div className="section-head">
          <h3>Core Categories</h3>
          <p className="muted">Curated inputs for quality, shelf life, and consistency.</p>
        </div>
        <div className="grid cards">
          {[
            { title: "Wheat & Flours", points: ["Kothumai Maavu", "Kilanghu Maavu", "Kothumai Ravai"] },
            { title: "Oils & Ghee", points: ["Gold Winner Sunflower Oil", "Refined Sunflower Oil", "Pure Ghee"] },
            { title: "Cocoa & Chocolates", points: ["Cocoa Powder", "Choco Compound", "Choco Chips"] },
            { title: "Spices & Masalas", points: ["Kulambu Milagai Thool", "Channa Masala", "Chat Masala"] },
            { title: "Sugars & Sweeteners", points: ["Rose Milk Mix", "Badam Milk Powder", "Sugar"] },
            { title: "Dry Fruits & Nuts", points: ["Mundhiri (Cashew)", "Badam (Almond)", "Raisins"] },
            { title: "Pasta & Grains", points: ["Savorit Pasta", "Sangu Pasta", "Macaroni"] },
            { title: "Packaging & Consumables", points: ["Biodegradable Bags", "Food Containers", "Plastic Spoons"] },
            { title: "Pulses & Lentils", points: ["Uruttu Ulundhu", "IR20 Rice", "Chinna Kadugu"] },
          ].map((cat, i) => (
            <article className="card" key={i}>
              <div>
                <h4>{cat.title}</h4>
                <ul>{cat.points.map((p, j) => <li key={j}>{p}</li>)}</ul>
              </div>
              <div className="card-actions"><a href="#items" className="link">Check availability →</a></div>
            </article>
          ))}
        </div>
      </section>

      <section id="industries" className="section alt">
        <div className="container">
          <div className="section-head">
            <h3>Industries We Serve</h3>
            <p className="muted">From artisan bakeries to volume producers and hospitality.</p>
          </div>
          <div className="grid industries">
            {[
              "Bakeries & Patisseries", "Sweet & Savoury Shops", "Hotels, Restaurants & Cafés",
              "Cloud Kitchens & Caterers", "Food Manufacturers", "Institutional Buyers"
            ].map((t, i) => (
              <div className="industry" key={i}>
                <span className="badge" aria-hidden="true">B2B</span>
                <div className="industry-title">{t}</div>
                <a href="#items" className="link sm">Enquire →</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="why" className="container section">
        <div className="section-head"><h3>Why Choose B2B Traders</h3></div>
        <div className="grid why">
          {[
            { t: "Quality Assured", d: "Batch-level checks, reputed brands, consistent specs." },
            { t: "Reliable Fulfilment", d: "Planned inventory + quick turnaround on priority SKUs." },
            { t: "Transparent Pricing", d: "Fair wholesale rates and long-term price stability." },
          ].map((f, i) => (
            <div className="why-item" key={i}>
              <div className="why-icon" aria-hidden="true">✓</div>
              <div>
                <h4>{f.t}</h4>
                <p className="muted">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ItemsPicker
        key={pickerKey}
        excelUrl="/items.xlsx"
        onSelectionChange={setSelectedItems}
      />

      <section id="contact" className="container section alt-section">
        <div className="contact">
          <div>
            <h3>Contact & Address</h3>
            <p className="muted">We're near Dharapuram Bus Stand (Sun Café backside).</p>
            <div className="contact-grid" role="list">
              <div className="contact-tile" role="listitem">
                <div className="tile-label">Phone</div>
                <a href={`tel:${PHONE}`} className="tile-value"> +91 90801 22817</a>
              </div>
              <div className="contact-tile" role="listitem">
                <div className="tile-label">Address</div>
                <address className="tile-value">
                  B2B Traders,<br />
                  299, B Chitharavithanpalayam, Anna Nagar,<br />
                  Dharapuram Bus Stand (near Sun Café, backside),<br />
                  Dharapuram, Tirupur District – 638657
                </address>
              </div>
              <div className="contact-tile" role="listitem">
                <div className="tile-label">Location</div>
                <a href={MAPS_URL} target="_blank" rel="noreferrer" className="tile-value">Open in Google Maps →</a>
              </div>
            </div>
          </div>
          <form
            ref={formRef}
            className="contact-form"
            onSubmit={onSubmit}
            noValidate
          >
            <h4>Request an Enquiry</h4>
            {!needCategories && selectedItems.length > 0 && (
              <details open className="enquiry-preview">
                <summary>
                  Selected items ({selectedItems.length}) — auto-attached to your enquiry
                </summary>
                <ul className="preview-list">
                  {selectedItems.map((it) => (
                    <li key={it.id}>
                      <div className="preview-name">{it.name}</div>
                      <div className="preview-meta">
                        {it.unit && <span>Unit: {it.unit}</span>}
                        {it.brand && <span>Brand: {it.brand}</span>}
                        <span>Qty: {it.qty}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div aria-live="polite" className={`form-status ${status.type || ""}`} style={{ minHeight: 24, marginBottom: status.msg ? 8 : 0 }}>
              {status.msg}
            </div>

            {/* ✅ 4. Apply the conditional style to the div */}
            <div className="form-grid" style={formGridStyle}>
              <div className="field">
                <input type="text" name="user_name" placeholder="Your Name *" autoComplete="name" aria-invalid={!!errors.user_name} aria-describedby="err_name" onBlur={handleBlur} />
                {errors.user_name && <small id="err_name" className="error-text">{errors.user_name}</small>}
              </div>
              <div className="field">
                <input type="tel" name="user_phone" inputMode="tel" placeholder="Phone *" autoComplete="tel" aria-invalid={!!errors.user_phone} aria-describedby="err_phone" onBlur={handleBlur} />
                {errors.user_phone && <small id="err_phone" className="error-text">{errors.user_phone}</small>}
              </div>
              <div className={`field ${!needCategories && !isMobile ? "col-span-2" : ""}`}>
                <input type="text" name="business_name" placeholder="Business Name *" autoComplete="organization" aria-invalid={!!errors.business_name} aria-describedby="err_biz" onBlur={handleBlur} />
                {errors.business_name && <small id="err_biz" className="error-text">{errors.business_name}</small>}
              </div>
              {needCategories && (
                <div className="field">
                  <MultiSelectDropdown
                    options={categoriesList}
                    value={selectedCategories}
                    onChange={setSelectedCategories}
                    placeholder="Select categories..."
                    error={errors.categories}
                    onValidate={handleCategoryChange}
                  />
                  {errors.categories && (
                    <small className="error-text" style={{ marginTop: '6px', display: 'block' }}>{errors.categories}</small>
                  )}
                </div>
              )}
            </div>
            <textarea name="message" rows={4} placeholder="Your requirements (optional)" />
            <input type="hidden" name="_replyto" value="b2btradersb2@gmail.com" />
            <input type="hidden" name="_template" value="table" />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_autoresponse" value="Thanks! We've received your enquiry and will get back shortly." />
            <input type="text" name="_honey" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Get in Touch"}
            </button>
            <p className="tiny muted">By submitting, you agree to be contacted for quotations and order support.</p>
          </form>
        </div>
      </section>

      <footer className="footer" aria-label="Footer">
        <div className="container footer-inner">
          <div className="footer-brand"><strong>B2B Traders</strong><span className="dot">•</span><span>Wholesale Raw Materials</span></div>
          <div className="footer-links">
            <a href={`tel:${PHONE}`}>Call</a>
            <a href={MAPS_URL} target="_blank" rel="noreferrer">Location</a>
            <a href="#categories">Categories</a>
            <a href="#getintouch">Enquire</a>
          </div>
          <div className="footer-copy">© {new Date().getFullYear()} B2B Traders. All rights reserved.</div>
        </div>
      </footer>

      {typeof window !== "undefined" && (
        <script dangerouslySetInnerHTML={{
          __html: `
            try {
              console.assert(${JSON.stringify(normalizeHeader(" Item Name "))} === 'item_name', 'normalizeHeader should snake_case and trim');
              console.assert(${JSON.stringify(normalizeHeader("M.R.P"))} === 'mrp', 'normalizeHeader should strip punctuation');
            } catch (e) { console.warn('Inline tests failed or skipped', e); }
          `,
        }} />
      )}
    </div>
  );
}